import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../sessionSlice";
import "./qu-add-question-modal.css";

const MAX_QUESTION_LENGTH = 200;
const MAX_OPTION_LENGTH = 50;
const MAX_OPTIONS = 8;
const MIN_OPTIONS = 2;
const DEFAULT_POINT = 10;
const DEFAULT_POINT_STR = String(DEFAULT_POINT);
const MAX_POINT = 100;

export default function AddQuestionModal({ isOpen, onClose, questionId }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(Array.from({ length: MIN_OPTIONS }, () => ""));
  const [optionPoints, setOptionPoints] = useState(
    Array.from({ length: MIN_OPTIONS }, () => DEFAULT_POINT_STR)
  );
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme } = useSelector((state) => state.theme);

  function fixEncoding(value) {
    return value;
  }

  const normalizePointInput = (raw) => {
    const str = String(raw ?? "").trim();
    const n = parseInt(str, 10);
    if (!Number.isFinite(n)) return DEFAULT_POINT_STR;
    return String(Math.max(0, n));
  };

  const normalizePointForSubmit = (raw) => {
    const str = String(raw ?? "").trim();
    const n = parseInt(str, 10);
    if (!Number.isFinite(n)) return DEFAULT_POINT;
    return Math.max(0, n);
  };

  const clampOptionLength = (value) => {
    const str = String(value ?? "");
    return str.length > MAX_OPTION_LENGTH ? str.slice(0, MAX_OPTION_LENGTH) : str;
  };

  useEffect(() => {
    if (!isOpen) return;
    if (questionId) {
      setLoading(true);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/getQuestionById`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ id: questionId }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.question) {
            const raw = data.question.question_name || "";
            setQuestion(
              raw.length > MAX_QUESTION_LENGTH
                ? raw.slice(0, MAX_QUESTION_LENGTH)
                : raw
            );
            const existingOptions = Array.isArray(data.options) ? data.options : [];
            const existingPoints = Array.isArray(data.optionPoints) ? data.optionPoints : [];
            const filledOptions = existingOptions.filter((opt) => String(opt || "").trim().length > 0);
            const nextLength = Math.max(MIN_OPTIONS, Math.min(MAX_OPTIONS, filledOptions.length));
            const nextOptions = Array.from({ length: nextLength }, (_, idx) =>
              clampOptionLength(existingOptions[idx] || "")
            );
            const nextPoints = Array.from({ length: nextLength }, (_, idx) => {
              return normalizePointInput(existingPoints[idx]);
            });
            setOptions(nextOptions);
            setOptionPoints(nextPoints);
          } else {
            Swal.fire("Error", "Failed to fetch question", "error");
            onClose();
          }
        })
        .catch(() => {
          Swal.fire("Error", "Failed to fetch question", "error");
          onClose();
        })
        .finally(() => setLoading(false));
    } else {
      setQuestion("");
      setOptions(Array.from({ length: MIN_OPTIONS }, () => ""));
      setOptionPoints(Array.from({ length: MIN_OPTIONS }, () => DEFAULT_POINT_STR));
    }
  }, [isOpen, questionId, adminToken, onClose]);

  const handleQuestionChange = (val) => {
    let newVal = val;
    if (val.length > MAX_QUESTION_LENGTH) {
      Swal.fire(
        "Limit Exceeded",
        `Question can be at most ${MAX_QUESTION_LENGTH} characters; extra text has been trimmed.`,
        "warning"
      );
      newVal = val.slice(0, MAX_QUESTION_LENGTH);
    }
    setQuestion(newVal);
  };

  const updateOption = (index, value) => {
    let nextValue = value;
    if (String(value ?? "").length > MAX_OPTION_LENGTH) {
      Swal.fire(
        "Limit Exceeded",
        `Option can be at most ${MAX_OPTION_LENGTH} characters; extra text has been trimmed.`,
        "warning"
      );
      nextValue = clampOptionLength(value);
    }
    setOptions((prev) => prev.map((v, i) => (i === index ? nextValue : v)));
  };

  const updatePoint = (index, value) => {
    setOptionPoints((prev) =>
      prev.map((v, i) => (i === index ? value : v))
    );
  };

  const normalizePointAtIndex = (index) => {
    setOptionPoints((prev) =>
      prev.map((v, i) => (i === index ? normalizePointInput(v) : v))
    );
  };

  const addOptionRow = () => {
    if (options.length >= MAX_OPTIONS) return;
    setOptions((prev) => [...prev, ""]);
    setOptionPoints((prev) => [...prev, DEFAULT_POINT_STR]);
  };

  const removeOptionRow = (index) => {
    if (options.length <= MIN_OPTIONS) return;
    setOptions((prev) => prev.filter((_, i) => i !== index));
    setOptionPoints((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    if (!question.trim()) {
      Swal.fire("Error", "Question is required", "error");
      return;
    }
    const trimmedOptions = options.map((o) => (o || "").trim());
    const nonEmptyCount = trimmedOptions.filter(Boolean).length;
    if (nonEmptyCount < MIN_OPTIONS) {
      Swal.fire("Error", `At least ${MIN_OPTIONS} options are required`, "error");
      return;
    }

    const overLimit = [];
    trimmedOptions.forEach((opt, idx) => {
      if (!opt) return;
      const pts = normalizePointForSubmit(optionPoints[idx]);
      if (pts > MAX_POINT) overLimit.push(idx + 1);
    });
    if (overLimit.length > 0) {
      Swal.fire(
        "Error",
        `Points cannot exceed ${MAX_POINT}. Please fix option(s): ${overLimit.join(", ")}.`,
        "error"
      );
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);

    const formData = new FormData();
    const sanitizedOptionPoints = optionPoints.map(normalizePointForSubmit);
    formData.append("question", question);
    formData.append("options", JSON.stringify(trimmedOptions));
    formData.append("optionPoints", JSON.stringify(sanitizedOptionPoints));
    formData.append("correctAnswer", "1");
    formData.append("currentTheme", currentTheme);
    if (questionId) formData.append("id", questionId);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/addNewQuestion`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
          body: formData,
        }
      );
      if (!res.ok) throw new Error("Request failed");
      Swal.fire(
        "Success",
        questionId ? "Question updated" : "Question added",
        "success"
      );
      window.location.reload();
      onClose();
    } catch (err) {
      Swal.fire("Error", "Failed to save question", "error");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  return (
    <>
      <div
        className="qu-modal-backdrop"
        onClick={() => {
          if (!submitting && !loading) onClose();
        }}
      />
      <div className="qu-modal">
        <h2>
          {loading
            ? "Loading..."
            : questionId
            ? "Edit Question"
            : "Add New Question"}
        </h2>

        {!loading && (
          <>
            <div className="qu-form-group">
              <label>Question name</label>
              <input
                type="text"
                value={fixEncoding(question)}
                onChange={(e) => handleQuestionChange(e.target.value)}
                placeholder="Enter prompt text"
                disabled={loading}
              />
              <div className="code">
                * Max length {MAX_QUESTION_LENGTH} characters.
              </div>
            </div>
            <div className="qu-form-group">
              <label>Options and points (up to 8)</label>
              {options.map((opt, idx) => (
                <div
                  key={`option-${idx}`}
                  style={{ display: "flex", gap: "8px", marginBottom: "8px" }}
                >
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => updateOption(idx, e.target.value)}
                    placeholder={`Option ${idx + 1}`}
                  />
                  <input
                    type="number"
                    min="0"
                    value={optionPoints[idx]}
                    onChange={(e) => updatePoint(idx, e.target.value)}
                    onBlur={() => normalizePointAtIndex(idx)}
                    placeholder="Points"
                    style={{ width: "90px" }}
                  />
                  {options.length > MIN_OPTIONS ? (
                    <button
                      type="button"
                      className="qu-cancel-btn"
                      onClick={() => removeOptionRow(idx)}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                className="qu-save-btn"
                onClick={addOptionRow}
                disabled={options.length >= MAX_OPTIONS}
              >
                Add Option
              </button>
              <div className="code">* Max length {MAX_OPTION_LENGTH} characters per option.</div>
              <div className="code">* Max {MAX_POINT} points per option.</div>
            </div>
            <div className="qu-actions">
              <button
                className="qu-cancel-btn"
                disabled={loading || submitting}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="qu-save-btn"
                disabled={loading || submitting}
                onClick={handleSubmit}
              >
                {submitting
                  ? "Saving..."
                  : questionId
                    ? "Update"
                    : "Save Question"}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
